import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjsUtils from '@attain-apps/rxjs-utils'
import * as rxjs from 'rxjs'

import type * as me from '../../../me'
import type { Feature } from '../feature'
import * as gql from '../gql'

const PEOPLE_INSIGHTS_BATCH_SIZE = 30

export const onLoadPeopleInsightsRequest = store.effects.create(
  (feature: Feature, meFeature: me.Feature) =>
    feature.actions.loadPeopleInsights.request.observe().pipe(
      rxjsUtils.latestFrom(action => ({
        action: rxjs.of(action),
        chart: feature.selectors.getChart({
          chartId: action.data.chartId,
        }).asObservable,
        context: meFeature.contexts.selectors.zoneContext.asObservable,
        nodes: (action.data.id
          ? feature.selectors
              .getNode({
                chartId: action.data.chartId,
                nodeId: action.data.id,
              })
              .project(node => (node ? [node] : []))
          : feature.selectors.getNodes({
              chartId: action.data.chartId,
            })
        ).asObservable,
      })),
      rxjs.switchMap(({ action, context, chart, nodes }) => {
        // if we don't have context, chart, or nodes, we can't proceed.
        if (!context || !chart || nodes.length === 0) {
          return rxjs.of(store.effects.SKIP)
        }

        const peopleInsightsLookup = chart.peopleInsightsLookup ?? {}
        const nodesToLoad = nodes.filter(
          node =>
            !peopleInsightsLookup[ents.core.display.tree.Node.Id.get(node)],
        )

        if (nodesToLoad.length === 0) {
          return rxjs.of(store.effects.SKIP)
        }

        const peopleInsightsById: Record<
          ents.core.display.tree.Node.Id,
          ents.core.display.tree.Node.PeopleInsights
        > = {}
        const failures: ents.core.display.tree.Node.Id[] = []

        return rxjs.from(nodesToLoad).pipe(
          // Group nodes into batches of 30 to limit parallel requests.
          rxjs.bufferCount(PEOPLE_INSIGHTS_BATCH_SIZE),
          // Run each batch sequentially; next batch waits for this one to finish.
          rxjs.concatMap(batch =>
            rxjs.from(batch).pipe(
              // Execute requests in parallel within the batch.
              rxjs.mergeMap(node => {
                const nodeId = ents.core.display.tree.Node.Id.get(node)
                return gql.queries.peopleInsights
                  .execute({
                    ...context,
                    query: JSON.stringify(node.peopleInsights),
                  })
                  .pipe(
                    rxjs.retry({ count: 3 }),
                    rxjs.tap(peopleInsights => {
                      peopleInsightsById[nodeId] = peopleInsights[0]
                    }),
                    rxjs.catchError(() => {
                      failures.push(nodeId)
                      return rxjs.EMPTY
                    }),
                  )
              }),
            ),
          ),
          rxjs.ignoreElements(),
          rxjs.endWith(
            feature.actions.loadPeopleInsights.success.from(action).create({
              request: action.data,
              response: { failures, peopleInsightsById },
            }),
          ),
        )
      }),
    ),
)
